import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { __ } from 'react-i18n';
import { FlexCol, FlexRowCenterVertical } from '../../Defaults/Flexbox/Flexbox';
import { Title } from '../General/Title';
import InfoRow from './InfoRow';
import { PriceInfo } from './PriceInfo';
import { CountSetter } from './CountSetter';
import { ButtonPrimary } from '../General/Button';
import { SvgIcon, IconType } from '../General/SvgIcon';
import { Select } from '../General/Select';

interface Good {
  good_id: number;
  attribs: Array<{
    attrib_id: number;
    attrib_value: string;
    value_id: number;
    attrib_name: string;
  }>;
}

interface SelectProps {
  name: string;
  value: string;
}

interface Props {
  title: string;
  shortDescription: string;
  plu: string;
  warranty: string;
  manufacturer: string;
  pckg: string;
  onStock: number;
  price: number | string;
  priceWithoutVat: number | string;
  currency: string;
  unit: string;
  count: number;
  addToCart: (selectedGoodId: number) => void;
  shareButton: any;
  handleCountChange: (newValue) => void;
  handleGoodChange: any;
  variants: Good[];
}

function RightUpper({
  title,
  shortDescription,
  plu,
  warranty,
  manufacturer,
  pckg,
  onStock,
  price,
  priceWithoutVat,
  currency,
  unit,
  count,
  addToCart,
  shareButton,
  handleCountChange,
  handleGoodChange,
  variants,
}: Props) {
  const [selectedGoodId, setSelectedGoodId] = React.useState(-1);

  const onChange = event => {
    const goodId = event.target.value;
    setSelectedGoodId(Number(goodId));
    handleGoodChange(Number(goodId));
  };

  const handleClick = () => {
    addToCart(selectedGoodId);
  };

  React.useEffect(() => {
    if (variants && variants.length > 0) {
      setSelectedGoodId(variants[0].good_id);
    }
  }, []);

  const optionsVariants: SelectProps[] = [];
  variants.forEach(v => {
    const good_id = v.good_id;
    if (v.attribs && v.attribs.length > 0) {
      v.attribs.forEach(a => {
        if (a.attrib_name === 'Veľkosť') {
          const obj: SelectProps = {
            name: `${a.attrib_name}  -  ${a.attrib_value}`,
            value: good_id.toString(),
          };
          optionsVariants.push(obj);
        }
      });
    }
  });

  // console.log(optionsVariants);

  const bottomPartDesktop = () => {
    return (
      <>
        <PriceInfo
          price={price}
          priceWithoutVat={priceWithoutVat}
          currency={currency}
        />
        {optionsVariants && optionsVariants.length > 0 && (
          <Variants>
            <Select
              value={selectedGoodId}
              options={optionsVariants}
              onChange={onChange}
            />
          </Variants>
        )}
        <PartWrapper>
          <CountSetter
            step={1}
            unit={unit}
            count={count}
            onChange={handleCountChange}
            onStock={onStock}
          />
          <CartButton onClick={handleClick} disabled={onStock <= 0}>
            <SvgIcon
              width={24}
              height={24}
              icon={IconType.newCart}
              fill={'#FFFFFF'}
              alt={__('Košík')}
              title={__('Košík')}
              marginRight={12}
            />
            {__('DO KOŠÍKA')}
          </CartButton>
        </PartWrapper>
      </>
    );
  };

  const bottomPartMobile = () => {
    return (
      <>
        <PartWrapper>
          <PriceInfo
            price={price}
            priceWithoutVat={priceWithoutVat}
            currency={currency}
          />
          <CountSetter
            step={1}
            unit={unit}
            count={count}
            onChange={handleCountChange}
            onStock={onStock}
          />
        </PartWrapper>
        {optionsVariants && optionsVariants.length > 0 && (
          <Variants>
            <Select
              value={selectedGoodId}
              options={optionsVariants}
              onChange={onChange}
            />
          </Variants>
        )}
        <CartButton onClick={handleClick} disabled={onStock <= 0}>
          <SvgIcon
            width={24}
            height={24}
            icon={IconType.newCart}
            fill={'#FFFFFF'}
            alt={__('Košík')}
            title={__('Košík')}
            marginRight={12}
          />
          {__('DO KOŠÍKA')}
        </CartButton>
      </>
    );
  };

  const Variants = styled.div`
    margin-top: ${rem(24)};
  `;

  const rightUpperContent = () => {
    return (
      <>
        <ProductTitle marginTop={0} marginBottom={24}>
          {title}{' '}
        </ProductTitle>
        {shortDescription && typeof shortDescription === 'string' && (
          <Description
            dangerouslySetInnerHTML={{ __html: `${shortDescription}` }}
          />
        )}
        <InfoRow title={__('Kód produktu')} value={plu} />
        {warranty ? <InfoRow title={__('Záruka')} value={warranty} /> : null}
        {manufacturer ? (
          <InfoRow title={__('Výrobca')} value={manufacturer} />
        ) : null}
        {pckg ? (
          <InfoRow title={__('Balenie')} value={pckg} hasBorderBottom={true} />
        ) : null}
        <InfoRow
          title={__('Skladom')}
          value={onStock}
          hasBorderBottom={false}
        />
        <MobilePartWrapper>{bottomPartMobile()}</MobilePartWrapper>
        <DesktopPartWrapper>{bottomPartDesktop()}</DesktopPartWrapper>
        {shareButton}
      </>
    );
  };
  return (
    <>
      <DesktopWrapper>{rightUpperContent()}</DesktopWrapper>
      <MobileWrapper>{rightUpperContent()}</MobileWrapper>
    </>
  );
}

const DesktopPartWrapper = styled.div`
  display: block;
  margin-top: ${rem(36)};

  ${({ theme }) => theme.mediab.s450`
    display: none;
  `}
`;

const MobilePartWrapper = styled.div`
  display: none;
  margin-top: ${rem(36)};

  ${({ theme }) => theme.mediab.s450`
    display: block;
  `}
`;
const Wrapper = styled(FlexCol)`
  width: 50%;
  margin: 0 ${rem(64)};

  ${({ theme }) => theme.mediab.l1050`
     margin: 0 ${rem(24)};
  `}

  ${({ theme }) => theme.mediab.m810`
    margin: 0;
    width: 100%;
  `}

  ${({ theme }) => theme.mediab.s450`
    margin: 0;
  `}
`;

const DesktopWrapper = styled(Wrapper)`
  ${({ theme }) => theme.mediab.l925`
    display: none;
  `}
`;

const MobileWrapper = styled(Wrapper)`
  display: none;

  ${({ theme }) => theme.mediab.l925`
   display: flex;
   max-width: ${rem(600)};
   margin: 0 auto ${rem(26)} auto;
  `}
`;

const Description = styled.div`
  margin-bottom: ${rem(24)};
  font-size: ${rem(14)};
  font-family: ${({ theme }) => theme.font.primary};
  line-height: ${rem(22)};
`;

const PartWrapper = styled(FlexRowCenterVertical)`
  margin-top: ${rem(16)};
  margin-bottom: ${rem(8)};
  justify-content: start;
  width: 100%;
`;

const CartButton = styled(ButtonPrimary)`
  width: 100%;

  &:disabled {
    background-color: #dcd6d6;
    color: ${({ theme }) => theme.colors.black};

    svg {
      fill: ${({ theme }) => theme.colors.black};

      * {
        fill: inherit;
      }
    }
  }

  ${({ theme }) => theme.mediab.s450`
    margin: ${rem(24)} auto;
  `}
`;

const ProductTitle = styled(Title)`
  font-family: ${({ theme }) => theme.font.secondary};
  color: ${({ theme }) => theme.colors.tertiary};
  font-weight: 700;

  ${({ theme }) => theme.mediab.m810`
    display: none;
  `};
`;

export { RightUpper as SanitasRightUpper };
